import { useState, useCallback, useEffect, useRef } from "react";

const useSubmenuHover = (sidebarCollapsed, sidebarSelector = ".ps-sidebar-root") => {
  const [hoveredSubmenu, setHoveredSubmenu] = useState(null);
  const hideTimeoutRef = useRef(null);

  // Clear any existing timeout and set new one
  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const scheduleHide = useCallback(
    (delay = 500) => {
      clearHideTimeout();
      const timeoutId = setTimeout(() => {
        setHoveredSubmenu(null);
        hideTimeoutRef.current = null;
      }, delay);
      hideTimeoutRef.current = timeoutId;
    },
    [clearHideTimeout]
  );

  const handleSubmenuMouseEnter = useCallback(
    (submenuId) => {
      if (sidebarCollapsed) {
        clearHideTimeout(); // Cancel any pending hide
        setHoveredSubmenu(submenuId);
      }
    },
    [sidebarCollapsed, clearHideTimeout]
  );

  const handleSubmenuMouseLeave = useCallback(() => {
    if (sidebarCollapsed) {
      scheduleHide(400); // Generous delay for all mouse speeds
    }
  }, [sidebarCollapsed, scheduleHide]);

  const handleSidebarMouseLeave = useCallback(() => {
    if (sidebarCollapsed) {
      scheduleHide(500); // Even longer delay when leaving sidebar
    }
  }, [sidebarCollapsed, scheduleHide]);

  const handleMenuItemMouseEnter = useCallback(
    (submenuId) => {
      if (sidebarCollapsed) {
        clearHideTimeout(); // Cancel any pending hide
        setHoveredSubmenu(submenuId);
      }
    },
    [sidebarCollapsed, clearHideTimeout]
  );

  const handleMenuItemMouseLeave = useCallback(() => {
    if (sidebarCollapsed) {
      scheduleHide(300); // Schedule hide when leaving menu item
    }
  }, [sidebarCollapsed, scheduleHide]);

  // Reset submenu state when sidebar is not collapsed
  useEffect(() => {
    if (!sidebarCollapsed) {
      clearHideTimeout();
      setHoveredSubmenu(null);
    }
  }, [sidebarCollapsed, clearHideTimeout]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  // Global mouse tracking - only hide when mouse is far from sidebar area
  useEffect(() => {
    const handleGlobalMouseMove = (e) => {
      if (sidebarCollapsed && hoveredSubmenu) {
        const sidebarElement = document.querySelector(sidebarSelector);
        if (sidebarElement) {
          const rect = sidebarElement.getBoundingClientRect();
          const buffer = 400; // Large buffer to include submenu dropdown and scrollbar

          // Only hide if mouse is completely outside the generous buffer area
          const isOutsideArea =
            e.clientX < rect.left - 30 || e.clientX > rect.right + buffer || e.clientY < rect.top - 30 || e.clientY > rect.bottom + 30;

          if (isOutsideArea) {
            clearHideTimeout(); // Clear any pending timeout
            setHoveredSubmenu(null); // Hide immediately when far outside
          }
        }
      }
    };

    if (sidebarCollapsed && hoveredSubmenu) {
      document.addEventListener("mousemove", handleGlobalMouseMove);
      return () => document.removeEventListener("mousemove", handleGlobalMouseMove);
    }
  }, [sidebarCollapsed, hoveredSubmenu, clearHideTimeout, sidebarSelector]);

  return {
    hoveredSubmenu,
    setHoveredSubmenu,
    handleSubmenuMouseEnter,
    handleSubmenuMouseLeave,
    handleSidebarMouseLeave,
    handleMenuItemMouseEnter,
    handleMenuItemMouseLeave,
    clearHideTimeout,
    scheduleHide,
  };
};

export default useSubmenuHover;
